import type { ReactElement } from "react"
import { useEffect, useRef } from "react"
import { navigate } from "gatsby"
import type { DomainCheck } from "@onestore/api/domainSearch"
import type { ButtonAtmProps } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import LabelAtm from "@onestore/hel/dist/components/atoms/LabelAtm"
import DomainSearchResultItemMol from "@onestore/hel/dist/components/molecules/DomainSearchResultItemMol"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import { isResponsePending } from "@onestore/onestore-store-common/http"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import SearchResultPrice from "@gatsby-plugin-domain-search/components/ResultsOrgSingle/SearchResultPrice"
import useButtonLoadingState from "@gatsby-plugin-domain-search/hooks/useButtonLoadingState"
import {
  buttonColor,
  searchResultExtensionColor,
} from "~/configs/checkoutConfig"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import { useCloudblueDomainInfo } from "~/hooks/useCloudblueDomainInfo"
import {
  sendBonusContinueEvent,
  sendBonusGotoBasketEvent,
} from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { getBasketRequestState } from "~/store/basket/selectors"
import { DOMAIN_STATUS } from "~/store/domain-search/constants"
import { useAppSelector } from "~/store/hooks"

type BonusSearchContentRowProps = {
  onClick: {
    (
      fqdn: DomainCheck.FQDN,
      planId: number,
      buttonId: string,
      ignoreHack: boolean
    ): void
  }
  onNextStepClick?: { (): void }
  result: DomainCheck.Result
  basketDomains: string[]
  buttonDataTestId?: string
  onDomainSelected?: (domainResult: DomainCheck.Result) => void
  selectedFqdns?: string[]
}

export default function BonusSearchContentRow({
  result,
  onClick,
  onNextStepClick = undefined,
  basketDomains = [],
  buttonDataTestId,
  onDomainSelected,
  selectedFqdns,
}: BonusSearchContentRowProps): ReactElement<BonusSearchContentRowProps> | null {
  const { plan_id, status, period, fqdn, extension, name } = result
  const buttonId = `bonus-search-button-${fqdn}`
  const isLoading = useButtonLoadingState(buttonId)
  const isAnyBasketPending = isResponsePending(
    useAppSelector(getBasketRequestState)
  )
  const isInFlightRef = useRef(false)
  const { domainLabel, tooltip } = useCloudblueDomainInfo(plan_id)
  const { currentPriceType } = usePriceTypeContext()
  const isSmallOnly = useBreakpoint("small", "medium")

  const periodInfo = period ? new PeriodInfo(period) : null
  const hasPromotion = periodInfo?.hasPromotion() ?? false
  const periodText = !isEmpty(period)
    ? ` ${_t(`periodName.searchDomainPrice.3`, {
        smart_count: periodInfo?.getPeriodLength(),
      })}`
    : ""

  useEffect(() => {
    if (!isLoading) {
      isInFlightRef.current = false
    }
  }, [isLoading])

  if (!period) {
    return null
  }

  const available = DOMAIN_STATUS.AVAILABLE === status

  // AX drawer flow: if the domain is selected, show the next step button, otherwise show the add button
  if (onDomainSelected) {
    const isSelected = selectedFqdns?.includes(fqdn) ?? false
    const selectionButtonText = isSelected
      ? onNextStepClick
        ? _t("preBasket.nextStep")
        : _t("configDrawer.summary.added")
      : _t("domainSearch.button.add")

    const handleSearchResultOnClick = isSelected
      ? () => onNextStepClick?.()
      : () => {
          if (!isEmpty(fqdn) && plan_id !== null) {
            onDomainSelected(result)
          }
        }

    return (
      <DomainSearchResultItemMol
        domain={{
          name: name,
          extension: `.${extension}`,
          extensionColor: searchResultExtensionColor,
        }}
        price={
          <SearchResultPrice
            periodInfo={periodInfo}
            priceType={currentPriceType}
            periodText={periodText}
            tooltip={tooltip}
            priceVariant={{
              type: PriceVariantType.SMALL,
              align: isSmallOnly ? "left" : "right",
            }}
          />
        }
        label={
          !isEmpty(domainLabel) || hasPromotion ? (
            <LabelAtm size="small" variant="notify" emphasis="medium">
              {hasPromotion ? _t("marketplace.discountTitle") : domainLabel}
            </LabelAtm>
          ) : undefined
        }
        hasSuccess={isSelected}
        isDisabled={!available}
        disabledMessage={
          !available ? _t("domainSearch.domainUnvailable") : undefined
        }
        button={
          {
            text: selectionButtonText,
            title: selectionButtonText,
            isDisabled: isSelected && !onNextStepClick,
            onClick: handleSearchResultOnClick,
            dataTestId: buttonDataTestId,
          } as ButtonAtmProps
        }
        overriddenSuccessButtonColor={buttonColor}
      />
    )
  }

  const isInBasket = !isEmpty(fqdn) && basketDomains.indexOf(fqdn) > -1

  const onButtonClick = () => {
    if (isLoading || isAnyBasketPending || isInFlightRef.current) {
      return
    }

    if (isInBasket) {
      if (onNextStepClick) {
        onNextStepClick()
        sendBonusContinueEvent()

        return
      }

      sendBonusGotoBasketEvent()
      navigate(url.getBasketUrl())
    }

    if (!fqdn || basketDomains.indexOf(fqdn) > -1 || plan_id === null) {
      return
    }

    isInFlightRef.current = true
    onClick(fqdn, plan_id, buttonId, true)
  }

  let buttonText = available
    ? _t("domainSearch.addToBasket")
    : _t("domainSearch.domainUnvailable")

  if (isInBasket) {
    buttonText = onNextStepClick
      ? _t("preBasket.nextStep")
      : _t("button.success")
  }

  if (!available && !isLoading) {
    buttonText = _t("domainSearch.domainUnvailable")
  }

  return (
    <DomainSearchResultItemMol
      domain={{
        name: name,
        extension: `.${extension}`,
        extensionColor: searchResultExtensionColor,
      }}
      price={
        <SearchResultPrice
          periodInfo={periodInfo}
          priceType={currentPriceType}
          periodText={periodText}
          tooltip={tooltip}
          priceVariant={{
            type: PriceVariantType.SMALL,
            align: isSmallOnly ? "left" : "right",
          }}
        />
      }
      isLoading={isLoading}
      label={
        domainLabel || hasPromotion ? (
          <LabelAtm size="small" variant="notify" emphasis="medium">
            {hasPromotion ? _t("marketplace.discountTitle") : domainLabel}
          </LabelAtm>
        ) : undefined
      }
      isDisabled={!available && !isLoading}
      disabledMessage={
        !available && !isLoading
          ? _t("domainSearch.domainUnvailable")
          : undefined
      }
      hasSuccess={isInBasket}
      button={
        {
          text: buttonText,
          title: buttonText,
          onClick: onButtonClick,
          dataTestId: buttonDataTestId,
          isDisabled: isAnyBasketPending && !isInBasket,
        } as ButtonAtmProps
      }
      overriddenSuccessButtonColor={buttonColor}
      hasNarrowLayout
    />
  )
}
